import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/apiConfig'; // Adjust the import path as needed
import { logger } from '../../utils/logger';
import { cacheUtils, optimizationCache } from '../../utils/cacheUtils';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  // Use a ref to track if initial auth check has completed
  const initialAuthCheckComplete = useRef(false);
  
  // Inactivity timeout settings
  const INACTIVITY_TIMEOUT = 90 * 60 * 1000; // 90 minutes in milliseconds
  const WARNING_TIMEOUT = 25 * 60 * 1000; // Show warning at 25 minutes (5 min before logout)
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const [rememberMe, setRememberMe] = useState(false);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);

  // Initialize remember me state from localStorage
  useEffect(() => {
    const storedRememberMe = localStorage.getItem('rememberMe') === 'true';
    setRememberMe(storedRememberMe);
  }, []);

  // Logout function - moved earlier to avoid circular dependency
  const logout = useCallback(() => {
    logger.log('Logging out user');
    const token = localStorage.getItem('authToken');
    if (token) {
      axios.post(API_ENDPOINTS.logout, {}, {
        headers: { Authorization: `Token ${token}` },
      }).catch(() => {});
    }
    
    // Clear user-specific cache before clearing user data
    if (currentUser?.id) {
      cacheUtils.clearUserSpecificCache(currentUser.id);
      optimizationCache.clearOptimizationCache(currentUser.id);
    }
    
    // Clear timers
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    
    // Clear storage and state
    localStorage.removeItem('authToken');
    localStorage.removeItem('rememberMe');
    setCurrentUser(null);
    setIsAuthenticated(false);
    setRememberMe(false);
    setAuthError(null);
    setShowInactivityWarning(false);
  }, [currentUser?.id]);

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    
    lastActivityRef.current = Date.now();
    setShowInactivityWarning(false);
    
    // Only set timeout if user is authenticated and remember me is false
    if (isAuthenticated && !rememberMe) {
      // Set warning timer (5 minutes before logout)
      warningTimerRef.current = setTimeout(() => {
        logger.log('Showing inactivity warning - 5 minutes until logout');
        setShowInactivityWarning(true);
      }, WARNING_TIMEOUT);
      
      // Set logout timer (full timeout)
      inactivityTimerRef.current = setTimeout(() => {
        logger.log('User inactive for 30 minutes, logging out...');
        logout();
        setAuthError('Session expired due to inactivity. Please log in again.');
      }, INACTIVITY_TIMEOUT);
    }
  }, [isAuthenticated, rememberMe, logout]);

  // Track user activity
  const trackActivity = useCallback(() => {
    if (isAuthenticated && !rememberMe) {
      resetInactivityTimer();
    }
  }, [isAuthenticated, rememberMe, resetInactivityTimer]);

  // Setup activity listeners
  useEffect(() => {
    if (isAuthenticated && !rememberMe) {
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      
      events.forEach(event => {
        document.addEventListener(event, trackActivity, true);
      });

      // Start the initial timer
      resetInactivityTimer();

      return () => {
        events.forEach(event => {
          document.removeEventListener(event, trackActivity, true);
        });
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }
        if (warningTimerRef.current) {
          clearTimeout(warningTimerRef.current);
        }
      };
    }
  }, [isAuthenticated, rememberMe, trackActivity, resetInactivityTimer]);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      // Skip if we've already done the initial check
      if (initialAuthCheckComplete.current) return;
      
      setIsLoading(true);
      setAuthError(null);
      
      try {
        const token = localStorage.getItem('authToken');
        logger.log('Initial auth check with token:', token ? 'Token exists' : 'No token');
        
        if (!token) {
          logger.log('No token found, user is not authenticated');
          setIsAuthenticated(false);
          setCurrentUser(null);
        } else {
          // We have a token, fetch user data from backend
          logger.log('Token found, fetching user data...');
          try {
            const response = await axios.get(API_ENDPOINTS.user, {
              headers: {
                'Authorization': `Token ${token}`
              }
            });
            
            logger.log('User data fetched successfully:', response.data);
            setCurrentUser(response.data);
            setIsAuthenticated(true);
          } catch (error) {
            logger.error('Failed to fetch user data:', error);
            // Token might be invalid, remove it
            localStorage.removeItem('authToken');
            setIsAuthenticated(false);
            setCurrentUser(null);
            setAuthError('Session expired. Please login again.');
          }
        }
      } catch (error) {
        logger.error('Auth check failed:', error);
        setAuthError(error.message);
        setIsAuthenticated(false);
        setCurrentUser(null);
      } finally {
        setIsLoading(false);
        initialAuthCheckComplete.current = true;
      }
    };
    
    checkAuthStatus();
  }, []);

  // This function will be called from your login page
  const login = async (userData, rememberMeOption = false) => {
    logger.log('Setting auth state with user data:', userData, 'Remember me:', rememberMeOption);
    
    try {
      // If userData contains a token, we'll use that
      if (userData?.token) {
        localStorage.setItem('authToken', userData.token);
      }
      
      // Set the current user from the userData
      if (userData?.user) {
        setCurrentUser(userData.user);
      }
      
      // Handle remember me option
      setRememberMe(rememberMeOption);
      localStorage.setItem('rememberMe', rememberMeOption.toString());
      
      // Set authenticated state
      setIsAuthenticated(true);
      setIsLoading(false);
      
      // Clear any previous auth errors
      setAuthError(null);
      
      return true;
    } catch (error) {
      logger.error('Login state update failed:', error);
      setAuthError(error.message);
      return false;
    }
  };

  // // Extend session when user responds to warning
  // const extendSession = useCallback(() => {
  //   logger.log('User extended session');
  //   setShowInactivityWarning(false);
  //   resetInactivityTimer();
  // }, [resetInactivityTimer]);

  // // Dismiss warning without extending (countdown continues)
  // const dismissWarning = useCallback(() => {
  //   setShowInactivityWarning(false);
  // }, []);

  // Function to refresh user data
  const refreshUserData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No auth token found');
      }
      
      const response = await axios.get(API_ENDPOINTS.user, {
        headers: {
          'Authorization': `Token ${token}`
        }
      });
      
      setCurrentUser(response.data);
      return response.data;
    } catch (error) {
      logger.error('Failed to refresh user data:', error);
      throw error;
    }
  };

  // For debugging
  useEffect(() => {
    logger.log('Auth state changed:', { 
      isAuthenticated, 
      isLoading,
      currentUser: currentUser ? 'User exists' : 'No user',
      hasToken: !!localStorage.getItem('authToken')
    });
  }, [isAuthenticated, isLoading, currentUser]);

  const value = {
    currentUser,
    isAuthenticated,
    isLoading,
    authError,
    login,
    logout,
    setCurrentUser,
    refreshUserData,
    rememberMe,
    setRememberMe,
    resetInactivityTimer,
    showInactivityWarning,
    // extendSession,
    // dismissWarning,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);